import { useCallback } from 'react';
import type { ImageDetails } from 'venn-api';
import { storeImageWithBasePath } from 'venn-api';
import { Notifications, NotificationType } from 'venn-ui-kit';
import { logExceptionIntoSentry } from 'venn-utils';
import type { RecoilState } from 'recoil';
import { useRecoilState } from 'recoil';
import {
  sectionPageBackgroundImage,
  sectionPageBackgroundImages,
  sectionPageImageUploading,
  studioImageUploading,
  viewCoverPageImage,
  viewImages,
} from 'venn-state';

type UseStudioImagesParams = {
  imagesState: RecoilState<ImageDetails[] | undefined>;
  backgroundImageState: RecoilState<string>;
  uploadingState: RecoilState<boolean>;
  basePath: string;
};

export const useStudioImages = () => {
  return useImages({
    imagesState: viewImages,
    backgroundImageState: viewCoverPageImage,
    uploadingState: studioImageUploading,
    basePath: 'report',
  });
};

export const useSectionPageImages = () => {
  return useImages({
    imagesState: sectionPageBackgroundImages,
    backgroundImageState: sectionPageBackgroundImage,
    uploadingState: sectionPageImageUploading,
    basePath: 'report-section',
  });
};

const useImages = ({ imagesState, backgroundImageState, uploadingState, basePath }: UseStudioImagesParams) => {
  const [images, setImages] = useRecoilState(imagesState);
  const [backgroundImage, setBackgroundImage] = useRecoilState(backgroundImageState);
  const [imageUploading, setImageUploading] = useRecoilState(uploadingState);

  const uploadReportImage = useCallback(
    async (input: HTMLInputElement | null, coverImage: boolean, onUpload?: (imageId: string) => void) => {
      if (!input || !input.files) {
        return;
      }

      setImageUploading(true);
      const toastId = Notifications.notify('Uploading image', NotificationType.LOADING);
      const image = input.files[0]!;
      await storeImageWithBasePath(image, basePath)
        .then((response) => {
          const imageId = response.content;

          const existingImages = images ?? [];
          const newImage: ImageDetails = {
            id: imageId,
            basePath,
            contentStream: undefined,
            contentType: 'image',
          };

          const newImages = [...existingImages, newImage];
          setImages(newImages);

          if (coverImage) {
            setBackgroundImage(imageId);
          }
          onUpload?.(imageId);

          setImageUploading(false);
          Notifications.notifyUpdate(toastId, 'Uploaded image', NotificationType.SUCCESS);
        })
        .catch(() => {
          setImageUploading(false);
          Notifications.notifyUpdate(toastId, 'Upload image failed', NotificationType.ERROR);
          logExceptionIntoSentry('Unable to upload image for user');
        });
    },
    [basePath, images, setBackgroundImage, setImageUploading, setImages],
  );

  return {
    imageUploading,
    uploadReportImage,
    images,
    backgroundImage,
    setBackgroundImage,
  };
};
